import { EmployeeFormValues } from 'src/modules/automations/components/organisms/employees/employee-form/types';
import { cleanValueForSchema } from 'src/modules/shared/utils/json-schema.util';

/**
 * Check if a cleaned value represents a non-empty object
 * @param value - The value to check
 * @returns True if the value is a non-empty object record
 */
const hasContent = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false;
  return Object.keys(value as Record<string, unknown>).length > 0;
};

/**
 * Sanitize a single tool by cleaning its configured inputs
 * @param tool - The tool configuration to sanitize
 * @returns The tool with cleaned configuredInputs (undefined if empty)
 */
const sanitizeTool = (
  tool: EmployeeFormValues['definition']['tools'][number]
): EmployeeFormValues['definition']['tools'][number] => {
  const cleanedInputs = cleanValueForSchema(undefined, tool.configuredInputs);

  return {
    ...tool,
    configuredInputs: hasContent(cleanedInputs) ? cleanedInputs : undefined,
  };
};

/**
 * Sanitizes the employee definition tools by cleaning configured inputs.
 * Removes empty or undefined configuredInputs objects to keep the definition lean.
 *
 * @param definition - The employee form definition containing tools
 * @returns The definition with sanitized tools
 */
export const sanitizeEmployeeDefinitionTools = (
  definition: EmployeeFormValues['definition']
): EmployeeFormValues['definition'] => {
  const sanitizedTools = (definition.tools || []).map(sanitizeTool);

  return {
    ...definition,
    tools: sanitizedTools,
  };
};
